import { useEffect, useRef, useState } from "react"
import { Head, Link, useForm, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"

const ACCEPTED_TYPES = [ "image/jpeg", "image/png", "image/webp" ]
const MAX_BYTES = 5 * 1024 * 1024

// Inline field-error component. Errors arriving via Inertia's
// `errors` prop (set by the controller's `redirect_to ..., inertia: {
// errors: { … } }`) flow into useForm.errors[fieldName]. Render right
// below the input so the user's eye stays on the offending field.
function FieldError({ message }) {
  if (!message) return null
  return (
    <p role="alert" className="text-xs text-red-600">{message}</p>
  )
}

function getInitials(name, email) {
  const source = (name || email || "").trim()
  if (!source) return "?"
  const parts = source.split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]).join("").toUpperCase()
}

function humanizeRemaining(deadlineIso, now) {
  if (!deadlineIso) return ""
  const ms = new Date(deadlineIso).getTime() - now
  if (ms <= 0) return "expired"
  const minutes = Math.floor(ms / 60000)
  if (minutes < 1) return "in less than a minute"
  if (minutes < 60) return `in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`
  const hours = Math.floor(minutes / 60)
  return `in ${hours} ${hours === 1 ? "hour" : "hours"}`
}

export default function AccountIndex({
  currentUserEmail,
  currentUserName,
  hasPassword,
  avatarUrl,
  headshotAttached,
  pendingEmailChange,
  error,
  notice,
}) {
  const [removePasswordOpen, setRemovePasswordOpen] = useState(false)
  const [removeAvatarOpen, setRemoveAvatarOpen] = useState(false)

  return (
    <>
      <Head title="Account" />
      <div className="min-h-screen bg-zinc-50 px-4 py-12">
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-600">
            Signed in as: <span className="font-medium text-zinc-900">{currentUserEmail}</span>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {notice && (
            <div
              role="status"
              className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
            >
              {notice}
            </div>
          )}

          <ProfileCard
            currentUserName={currentUserName}
            currentUserEmail={currentUserEmail}
            avatarUrl={avatarUrl}
            headshotAttached={headshotAttached}
            onRequestRemoveAvatar={() => setRemoveAvatarOpen(true)}
          />

          <EmailCard
            currentUserEmail={currentUserEmail}
            pendingEmailChange={pendingEmailChange}
          />

          <PasswordSection
            hasPassword={hasPassword}
            onRequestRemovePassword={() => setRemovePasswordOpen(true)}
          />

          <div className="pt-4 text-center">
            <Link
              href="/logout"
              method="post"
              as="button"
              className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              Sign out
            </Link>
          </div>
        </div>
      </div>

      <RemovePasswordDialog open={removePasswordOpen} onOpenChange={setRemovePasswordOpen} />
      <RemoveAvatarDialog open={removeAvatarOpen} onOpenChange={setRemoveAvatarOpen} />
    </>
  )
}

// ---------------------------------------------------------------
// Profile card — name + avatar, single combined form
// ---------------------------------------------------------------

function ProfileCard({
  currentUserName,
  currentUserEmail,
  avatarUrl,
  headshotAttached,
  onRequestRemoveAvatar,
}) {
  // Form initializes WITHOUT a headshot key. We add it via setData only
  // when a real File is selected. This is defense layer 1 against the
  // article-image-corruption pattern: a stale `headshot: null` in form
  // data would cause Active Storage to detach the existing attachment
  // on a name-only PATCH.
  const form = useForm({ account: { name: currentUserName || "" } })
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const setName = (value) => {
    form.setData("account", { ...form.data.account, name: value })
  }

  const handleFile = (event) => {
    setPreviewError(null)
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setPreviewError("Pick a JPEG, PNG, or WebP image.")
      event.target.value = ""
      return
    }
    if (file.size > MAX_BYTES) {
      setPreviewError("That image is over 5MB. Pick a smaller one.")
      event.target.value = ""
      return
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(URL.createObjectURL(file))
    form.setData("account", { ...form.data.account, headshot: file })
  }

  const submit = (event) => {
    event.preventDefault()
    form.patch("/account/profile", {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
        // Reset the form's headshot key so a follow-up name-only edit
        // doesn't accidentally re-submit the prior file.
        const next = { ...form.data.account }
        delete next.headshot
        form.setData("account", next)
        if (fileInputRef.current) fileInputRef.current.value = ""
      },
    })
  }

  const showAvatarImage = previewUrl || (avatarUrl && headshotAttached) || avatarUrl
  const initials = getInitials(currentUserName, currentUserEmail)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your name and avatar.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-5">
          <div className="flex items-center gap-4">
            {showAvatarImage ? (
              <img
                src={previewUrl || avatarUrl}
                alt="Your avatar"
                className="h-20 w-20 rounded-full object-cover ring-1 ring-zinc-200"
              />
            ) : (
              <div
                role="img"
                aria-label="No avatar set"
                className="flex h-20 w-20 items-center justify-center rounded-full bg-zinc-200 text-lg font-semibold text-zinc-600"
              >
                {initials}
              </div>
            )}

            <div className="flex-1 space-y-2">
              <Label htmlFor="avatar-input">Avatar</Label>
              <input
                ref={fileInputRef}
                id="avatar-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFile}
                className="block w-full text-sm text-zinc-600 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-zinc-800"
              />
              <p className="text-xs text-zinc-500">JPEG, PNG, or WebP. Up to 5MB.</p>
              {previewError && (
                <p role="alert" className="text-xs text-red-600">{previewError}</p>
              )}
              {previewUrl && (
                <p role="status" className="text-xs text-emerald-700" aria-live="polite">
                  New avatar ready to save.
                </p>
              )}
              {headshotAttached && !previewUrl && (
                <button
                  type="button"
                  onClick={onRequestRemoveAvatar}
                  className="text-xs text-zinc-500 underline-offset-2 hover:text-zinc-900 hover:underline"
                >
                  Remove avatar
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-name">Name</Label>
            <Input
              id="account-name"
              type="text"
              value={form.data.account.name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              aria-invalid={form.errors.name ? "true" : undefined}
            />
            <FieldError message={form.errors.name} />
          </div>

          <Button type="submit" className="w-full" disabled={form.processing}>
            {form.processing ? "Saving..." : "Save profile"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------
// Email card — state machine for request / pending / cancel
// ---------------------------------------------------------------

function EmailCard({ currentUserEmail, pendingEmailChange }) {
  const [now, setNow] = useState(() => Date.now())
  const [showForm, setShowForm] = useState(false)
  const form = useForm({ new_email: "" })

  useEffect(() => {
    if (!pendingEmailChange?.deadline) return undefined
    const interval = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(interval)
  }, [pendingEmailChange?.deadline])

  const submit = (event) => {
    event.preventDefault()
    form.post("/account/email", {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setShowForm(false)
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email</CardTitle>
        <CardDescription>
          Your sign-in email. Changing it sends a verification link to the new address — your current
          email keeps working until you confirm from that link.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-zinc-500">Current</p>
              <p className="font-medium text-zinc-900">{currentUserEmail}</p>
            </div>
            {!pendingEmailChange && !showForm && (
              <Button type="button" variant="outline" onClick={() => setShowForm(true)}>
                Change email
              </Button>
            )}
          </div>

          {pendingEmailChange && (
            <PendingEmailChange pendingEmailChange={pendingEmailChange} now={now} />
          )}

          {!pendingEmailChange && showForm && (
            <form onSubmit={submit} className="space-y-3 border-t border-zinc-200 pt-4">
              <div className="space-y-2">
                <Label htmlFor="new-email">New email</Label>
                <Input
                  id="new-email"
                  type="email"
                  required
                  value={form.data.new_email}
                  onChange={(e) => form.setData("new_email", e.target.value)}
                  autoComplete="email"
                  aria-invalid={form.errors.new_email ? "true" : undefined}
                />
                <FieldError message={form.errors.new_email} />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={form.processing}>
                  {form.processing ? "Sending..." : "Send verification email"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset()
                    setShowForm(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function PendingEmailChange({ pendingEmailChange, now }) {
  const cancel = () => {
    if (!window.confirm("Cancel the pending email change?")) return
    router.delete("/account/email", { preserveScroll: true })
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
      <p className="font-medium text-amber-900">Verification pending</p>
      <p className="mt-1 text-amber-800">
        Verification email sent to <span className="font-mono">{pendingEmailChange.login}</span>.
        Link expires {humanizeRemaining(pendingEmailChange.deadline, now)}.
      </p>
      <button
        type="button"
        onClick={cancel}
        className="mt-2 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
      >
        Cancel pending change
      </button>
    </div>
  )
}

// ---------------------------------------------------------------
// Password section — verbatim 002-plan UI, repathed to /account/password
// ---------------------------------------------------------------

function PasswordSection({ hasPassword, onRequestRemovePassword }) {
  return hasPassword ? (
    <ChangePasswordCard onRemoveClick={onRequestRemovePassword} />
  ) : (
    <SetPasswordCard />
  )
}

function SetPasswordCard() {
  const form = useForm({ password: "" })

  const submit = (event) => {
    event.preventDefault()
    form.post("/account/password", {
      onSuccess: () => form.reset("password"),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Set a password</CardTitle>
        <CardDescription>
          Adding a password lets you sign in without waiting for a magic-link email.
          Magic links keep working — passwords are an option, not a replacement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={form.data.password}
              onChange={(event) => form.setData("password", event.target.value)}
              aria-invalid={form.errors.password ? "true" : undefined}
            />
            <p className="text-xs text-zinc-500">Minimum 8 characters.</p>
            <FieldError message={form.errors.password} />
          </div>
          <Button type="submit" className="w-full" disabled={form.processing}>
            {form.processing ? "Saving..." : "Save password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function ChangePasswordCard({ onRemoveClick }) {
  const form = useForm({ current_password: "", password: "" })

  const submit = (event) => {
    event.preventDefault()
    form.patch("/account/password", {
      onSuccess: () => form.reset(),
    })
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Change your password</CardTitle>
          <CardDescription>
            Enter your current password to set a new one.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current password</Label>
              <Input
                id="current_password"
                type="password"
                autoComplete="current-password"
                required
                value={form.data.current_password}
                onChange={(event) => form.setData("current_password", event.target.value)}
                aria-invalid={form.errors.current_password ? "true" : undefined}
              />
              <FieldError message={form.errors.current_password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={form.data.password}
                onChange={(event) => form.setData("password", event.target.value)}
                aria-invalid={form.errors.password ? "true" : undefined}
              />
              <p className="text-xs text-zinc-500">Minimum 8 characters.</p>
              <FieldError message={form.errors.password} />
            </div>
            <Button type="submit" className="w-full" disabled={form.processing}>
              {form.processing ? "Saving..." : "Save changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Remove password</CardTitle>
          <CardDescription>
            Revert to magic-link-only sign-in. You can set a new password later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onRemoveClick}
          >
            Remove password
          </Button>
        </CardContent>
      </Card>
    </>
  )
}

// ---------------------------------------------------------------
// Confirm dialogs
// ---------------------------------------------------------------

function RemovePasswordDialog({ open, onOpenChange }) {
  const form = useForm({ current_password: "" })

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  const submit = (event) => {
    event.preventDefault()
    form.delete("/account/password", {
      onSuccess: () => handleClose(false),
    })
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Remove password?</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            After removing your password, you&rsquo;ll sign in with magic links only.
            You can set a new password later if you change your mind.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <form onSubmit={submit} className="space-y-4 px-4 pb-4 sm:px-0 sm:pb-0">
          <div className="space-y-2">
            <Label htmlFor="confirm_current_password">Current password</Label>
            <Input
              id="confirm_current_password"
              type="password"
              autoComplete="current-password"
              required
              value={form.data.current_password}
              onChange={(event) => form.setData("current_password", event.target.value)}
              aria-invalid={form.errors.current_password ? "true" : undefined}
            />
            <FieldError message={form.errors.current_password} />
          </div>
          <ResponsiveDialogFooter>
            <Button
              type="submit"
              variant="destructive"
              disabled={form.processing}
            >
              {form.processing ? "Removing..." : "Remove password"}
            </Button>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Keep my password
              </Button>
            </ResponsiveDialogClose>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function RemoveAvatarDialog({ open, onOpenChange }) {
  const submit = () => {
    router.delete("/account/avatar", {
      preserveScroll: true,
      onFinish: () => onOpenChange(false),
    })
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Remove your avatar?</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Your avatar will be deleted. You can upload a new one any time.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <ResponsiveDialogFooter className="px-4 pb-4 sm:px-0 sm:pb-0">
          <Button type="button" variant="destructive" onClick={submit}>
            Remove avatar
          </Button>
          <ResponsiveDialogClose asChild>
            <Button type="button" variant="outline">
              Keep avatar
            </Button>
          </ResponsiveDialogClose>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
